import React, { type FC } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AssetBadge } from 'venn-components';
import { GetColor, Icon, TooltipPosition, Tooltip } from 'venn-ui-kit';
import { navigateToManageDataPage, useHasFF } from 'venn-utils';
import { cn } from '@/lib/utils';
import type { SearchResultWithUIState } from './types';
import { getAnalysisRouteForItem, MASTER_PORTFOLIO_NO_DELETION_MESSAGE } from './utils';
import ItemOverview from './ItemOverview';

interface NameCellProps {
  item: SearchResultWithUIState;
  trackSelectionMade: (item: SearchResultWithUIState) => void;
}

const NameCell: FC<React.PropsWithChildren<NameCellProps>> = ({ item, trackSelectionMade }) => {
  const hasDefaultManageDataEndpointFF = useHasFF('rcm_manage_default_endpoint_ff');
  const history = useHistory();

  const navigateToRouteForItem = (item: SearchResultWithUIState) => {
    trackSelectionMade(item);
    const subject = { portfolioId: item.portfolioId, fundId: item.fundId };
    navigateToManageDataPage(history, subject, 'Library', false);
  };

  return (
    <StyledName data-testid={item.master ? 'master' : undefined}>
      {item.master && (
        <StyledTooltip maxWidth={310} position={TooltipPosition.Top} content={MASTER_PORTFOLIO_NO_DELETION_MESSAGE}>
          <Icon type="lock" />
        </StyledTooltip>
      )}

      <StyledTooltip maxWidth={400} className="min-w-0" content={<ItemOverview item={item} />}>
        <div className={cn('truncate print:whitespace-normal', item.master ? 'italic' : '')}>
          {item.isNew && <NewItemLabel>New</NewItemLabel>}

          {hasDefaultManageDataEndpointFF ? (
            <span
              role="link"
              tabIndex={0}
              onClick={() => navigateToRouteForItem(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigateToRouteForItem(item);
              }}
              data-testid="library-name"
            >
              {item.name}
            </span>
          ) : (
            <Link
              onClick={() => trackSelectionMade(item)}
              to={getAnalysisRouteForItem(item)}
              data-testid="library-name"
            >
              {item.name}
            </Link>
          )}
        </div>
      </StyledTooltip>

      <AssetBadge assetType={item.assetType} size={14} />
    </StyledName>
  );
};

export default NameCell;

const StyledName = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  font-weight: bold;
  color: ${GetColor.Primary.Dark};
  position: absolute;
  inset: 0 5px 0 0;
`;

const NewItemLabel = styled.span`
  vertical-align: middle;
  border-radius: 4px;
  font-size: 11px;
  text-align: center;
  color: ${GetColor.White};
  display: inline;
  background: ${GetColor.Primary.Main};
  padding: 1px 5px;
  margin-right: 4px;
  font-style: normal;
`;

const StyledTooltip = styled(Tooltip)`
  h1 {
    font-weight: bold;
    font-size: 1.5rem;
    margin: 0 0 0.67em 0;
  }

  .footer {
    display: block;
    margin-top: 21px;
    margin-bottom: 12px;
    color: ${GetColor.Primary.Main};

    i {
      padding-left: 3px;
    }
  }

  p {
    margin: 0;
    min-width: 240px;
  }

  .tooltip-value {
    float: right;
    font-weight: bold;
    padding-left: 13px;
  }

  /* set relative position to the tooltip ContentContainer (IE Fix) */
  & > div > div {
    position: relative;
  }
`;
